/**
 * Cart Line Item Variant Picker Component
 *
 * Handles variant changes within cart line items using WeakMaps for private data.
 *
 * @extends HTMLElement
 */

// WeakMaps for private data (one WeakMap per component instance)
const privateData = new WeakMap();

class CartLineItemVariantPicker extends HTMLElement {
  constructor() {
    super();

    // Bind event handlers
    this.handleOptionChange = this.handleOptionChange.bind(this);
  }

  connectedCallback() {
    // Initialize private data for this instance in connectedCallback
    // to ensure element is fully created
    if (!privateData.has(this)) {
      privateData.set(this, {
        lineKey: null,
        currentVariantId: null,
        quantity: null,
        productVariants: []
      });
    }

    const data = privateData.get(this);

    // Cache references to data attributes
    data.lineKey = this.getAttribute("data-line-key");
    data.currentVariantId = this.getAttribute("data-current-variant-id");
    data.quantity = parseInt(this.getAttribute("data-quantity"));

    // Load product variants from JSON
    const variantsScript = this.querySelector('[data-product-variants]');
    if (variantsScript) {
      try {
        data.productVariants = JSON.parse(variantsScript.textContent);
      } catch (error) {
        console.error("Failed to parse product variants:", error);
        data.productVariants = [];
      }
    }

    // Listen for changes on select dropdown
    const selectElement = this.querySelector('.select');
    if (selectElement) {
      selectElement.addEventListener('change', this.handleOptionChange);
    }

    // Check if this item was just changed (highlight it)
    this._checkForHighlight();
  }

  /**
   * Check if this item should be highlighted after a variant change
   * @private
   */
  _checkForHighlight() {
    const changedVariantId = sessionStorage.getItem('cart-variant-changed');
    if (changedVariantId) {
      const data = privateData.get(this);
      if (data.currentVariantId == changedVariantId) {
        // Clear the flag
        sessionStorage.removeItem('cart-variant-changed');

        // Highlight this line item
        const lineItem = this.closest('line-item');
        if (lineItem) {
          lineItem.classList.add('line-item--just-changed');

          // Scroll to this item smoothly
          lineItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

          // Remove highlight after animation
          setTimeout(() => {
            lineItem.classList.remove('line-item--just-changed');
          }, 2000);
        }
      }
    }
  }

  disconnectedCallback() {
    // CRITICAL: Clean up event listeners to prevent memory leaks
    const selectElement = this.querySelector('.select');
    if (selectElement) {
      selectElement.removeEventListener('change', this.handleOptionChange);
    }

    // Clean up private data
    privateData.delete(this);
  }

  /**
   * Handle option change from select dropdown
   */
  handleOptionChange(event) {
    const selectedOptionValue = event.target.value;

    if (!selectedOptionValue) {
      console.warn("No option value found");
      return;
    }

    // Find the variant that matches this option value
    const newVariant = this._findVariantByOptions(selectedOptionValue);

    if (!newVariant) {
      console.warn("No matching variant found for option:", selectedOptionValue);
      return;
    }

    const data = privateData.get(this);
    if (newVariant.id == data.currentVariantId) {
      return;
    }

    this._onVariantChange(newVariant);
  }

  /**
   * Find variant that matches the selected option value
   * @private
   */
  _findVariantByOptions(selectedOptionValue) {
    const data = privateData.get(this);
    const variants = data.productVariants;

    if (!variants || variants.length === 0) {
      return null;
    }

    // Find variant where any option matches the selected value
    return variants.find(variant => {
      return variant.option1 === selectedOptionValue ||
             variant.option2 === selectedOptionValue ||
             variant.option3 === selectedOptionValue;
    });
  }

  /**
   * Handle variant change by updating cart
   * @private
   */
  async _onVariantChange(newVariant) {
    // Check if variant is available
    if (!newVariant.available) {
      this._handleError("This variant is not available");
      return;
    }

    await this._changeLineItemVariant(newVariant.id);
  }

  /**
   * Change line item variant via Shopify Cart API
   * @private
   */
  async _changeLineItemVariant(newVariantId) {
    const data = privateData.get(this);

    // Dispatch loading start event
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    const lineItem = this.closest("line-item");
    lineItem?.dispatchEvent(new CustomEvent("line-item:will-change", {
      bubbles: true,
      detail: { targetVariantId: newVariantId }
    }));

    // Gather sections to bundle for refresh
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    try {
      // Step 1: Remove the old variant from cart
      const removeResponse = await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          id: data.lineKey,
          quantity: 0,
          sections: sectionsToBundle.join(",")
        })
      });

      if (!removeResponse.ok) {
        throw new Error("Failed to remove old variant");
      }

      // Step 2: Add the new variant with the same quantity
      // Add a timestamp property to help maintain order consistency
      const addResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          id: newVariantId,
          quantity: data.quantity,
          properties: {
            '_order_timestamp': Date.now().toString()
          },
          sections: sectionsToBundle.join(",")
        })
      });

      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));

      if (!addResponse.ok) {
        const errorContent = await addResponse.json();
        const errorMessage = errorContent["description"] || errorContent["message"] || "Failed to update variant";

        // Restore the old variant
        const restoreResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            id: data.currentVariantId,
            quantity: data.quantity
          })
        });

        if (restoreResponse.ok) {
          const restoreResponseJson = await restoreResponse.json();

          // Fetch the full cart and merge with sections from restore response
          const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
          cartContent["sections"] = restoreResponseJson["sections"];

          document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
            bubbles: true,
            detail: {
              baseEvent: "line-item:variant-change-error",
              cart: cartContent
            }
          }));
        }

        this._handleError(errorMessage);
      } else {
        // Success - get the response with sections
        const addResponseJson = await addResponse.json();

        // Fetch the full cart and merge with sections from add response
        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent["sections"] = addResponseJson["sections"];

        // Update internal state - Note: line item key will be different after add
        data.currentVariantId = newVariantId;
        this.setAttribute("data-current-variant-id", newVariantId);

        // Store the new variant ID in sessionStorage to highlight it after refresh
        sessionStorage.setItem('cart-variant-changed', newVariantId);

        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "line-item:variant-change",
            cart: cartContent
          }
        }));
      }
    } catch (error) {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      console.error("Error changing variant:", error);
      this._handleError("An error occurred while changing the variant");
    }
  }

  /**
   * Handle and display error message
   * @private
   */
  _handleError(message) {
    // Remove existing error messages
    this.parentElement.querySelector('[role="alert"]')?.remove();

    // Error icon SVG
    const errorSvg = `<svg width="13" height="13" fill="none" viewBox="0 0 13 13">
        <circle cx="6.5" cy="6.5" r="6.5" fill="#BF1515"/>
        <path fill="#fff" d="M6.75 7.97a.387.387 0 0 1-.3-.12.606.606 0 0 1-.12-.34l-.3-3.82c-.02-.247.033-.443.16-.59.127-.153.313-.23.56-.23.24 0 .42.077.54.23.127.147.18.343.16.59l-.3 3.82a.522.522 0 0 1-.12.34.344.344 0 0 1-.28.12Zm0 2.08a.744.744 0 0 1-.55-.21.751.751 0 0 1-.2-.54c0-.213.067-.387.2-.52.14-.14.323-.21.55-.21.233 0 .413.07.54.21.133.133.2.307.2.52 0 .22-.067.4-.2.54-.127.14-.307.21-.54.21Z"/>
      </svg>`;

    // Insert error message
    this.insertAdjacentHTML("afterend", `<p class="h-stack gap-2 justify-center text-xs" role="alert">${errorSvg} ${message}</p>`);

    // Auto-remove error after 3 seconds
    setTimeout(() => {
      this.parentElement.querySelector('[role="alert"]')?.remove();
    }, 3000);
  }
}

// Register the custom element
if (!window.customElements.get("cart-line-item-variant-picker")) {
  window.customElements.define("cart-line-item-variant-picker", CartLineItemVariantPicker);
}
